import { forwardRef, useState } from "react";
import type { ForwardedRef, ReactNode } from "react";
import { Collapse } from "../../molecules/Collapse";
import {
  ChevronDownIcon,
  SearchIcon,
  XIcon,
  type IconComponent,
} from "../../lib/icons";
import { TopBarButton } from "./TopBar";
import { cn } from "../../lib/cn";

export interface SideNavItem {
  key: string;
  text: string;
  /** Leading icon (leaf items only — group headers show no icon, like the portal). */
  icon?: IconComponent;
  /** Sub-items — presence makes this item a collapsible group. */
  items?: SideNavItem[];
  /** Active route marker — selected wash + primary indicator. */
  active?: boolean;
  onClick?: () => void;
  /** Draws attention with accent styling (e.g. "Getting Started"). */
  highlight?: boolean;
  /** Row-end slot on leaf items — captions like the Dashboard "Soon" teaser. */
  trailing?: ReactNode;
  /** Explicit row test id — defaults to expanded-subnav-/expanded-nav- + text slug. */
  testId?: string;
}

export interface SideNavSection {
  key: string;
  /** Optional static label above the section's items. */
  label?: string;
  items: SideNavItem[];
  /** Footer sections stay pinned below the scrolling primary navigation. */
  placement?: "main" | "footer";
}

/** Test-id slug — the portal convention (e.g. "Feature Switches" → "feature-switches"). */
function testIdSlug(text: string) {
  return text.toLowerCase().replace(/\s+/g, "-");
}

export interface SideNavProps {
  /** Nav model, rendered top to bottom. */
  sections: SideNavSection[];
  /** Logo row at the top — height matches the top bar. */
  header?: ReactNode;
  /** Keys of groups that ARE collapsed (controlled — only explicitly-collapsed keys are tracked, everything defaults open). */
  collapsedKeys?: string[];
  /** Uncontrolled initial collapsed group keys. */
  defaultCollapsedKeys?: string[];
  onToggleCollapsed?: (key: string) => void;
  /** Overlay drawer open state (below lg only — the desktop column ignores this). */
  open?: boolean;
  /** Closes the overlay drawer (backdrop and close button). */
  onClose?: () => void;
  /** Hides the desktop column entirely (below lg this has no effect). Collapsing hides the sidebar — no icon rail. */
  collapsed?: boolean;
  /** Opens search (the command palette) — renders an input-styled trigger above the items, portal parity. */
  onSearch?: () => void;
  /** Test id for the docked column; the overlay drawer derives `${testId}-drawer`. */
  testId?: string;
  /** Search trigger test id (default "sidenav-search"). */
  searchTestId?: string;
  className?: string;
}

/** Group collapse state — controlled `collapsedKeys` wins; otherwise uncontrolled with the same "explicitly-collapsed" semantics. */
function useCollapsedGroups({
  collapsedKeys,
  defaultCollapsedKeys = [],
  onToggleCollapsed,
}: Pick<
  SideNavProps,
  "collapsedKeys" | "defaultCollapsedKeys" | "onToggleCollapsed"
>) {
  const [internal, setInternal] = useState<string[]>(defaultCollapsedKeys);
  const active = collapsedKeys ?? internal;
  const isCollapsed = (key: string) => {
    return active.includes(key);
  };
  const toggle = (key: string) => {
    if (collapsedKeys === undefined) {
      setInternal((prev) => {
        return prev.includes(key)
          ? prev.filter((k) => k !== key)
          : [...prev, key];
      });
    }
    onToggleCollapsed?.(key);
  };
  return { isCollapsed, toggle };
}

/** Left indicator bar — shared by active and highlighted rows. */
const indicatorClasses =
  "before:absolute before:left-0 before:top-1/2 before:h-4 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-surface-primary";

function SideNavLeaf({ item }: { item: SideNavItem }) {
  const Icon = item.icon;
  const highlighted = Boolean(item.highlight) && !item.active;
  return (
    <button
      type="button"
      onClick={item.onClick}
      aria-current={item.active ? "page" : undefined}
      data-testid={item.testId ?? `expanded-subnav-${testIdSlug(item.text)}`}
      className={cn(
        "relative flex min-h-(--control-height) w-full items-center gap-2.5 rounded-md px-2.5 type-body-md outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-border-focused",
        (item.active || highlighted) && indicatorClasses,
        item.active
          ? "bg-surface-selected font-medium text-content-accent"
          : highlighted
            ? "bg-surface-primary-soft text-content-accent hover:bg-surface-selected"
            : "text-shell-on-secondary hover:bg-surface-pressed hover:text-shell-on",
      )}
    >
      {Icon ? <Icon className="size-4.5 shrink-0" aria-hidden="true" /> : null}
      <span className="min-w-0 truncate text-left">{item.text}</span>
      {item.trailing ? (
        <span className="ml-auto shrink-0 type-caption text-shell-on-secondary">
          {item.trailing}
        </span>
      ) : null}
    </button>
  );
}

function SideNavGroup({
  item,
  isCollapsed,
  onToggle,
}: {
  item: SideNavItem;
  isCollapsed: boolean;
  onToggle: (key: string) => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={() => {
          onToggle(item.key);
        }}
        aria-expanded={!isCollapsed}
        data-testid={item.testId ?? `expanded-nav-${testIdSlug(item.text)}`}
        className="flex w-full items-center gap-0.5 rounded-md px-2.5 pb-0.5 pt-3 type-overline font-semibold text-shell-on-secondary outline-none transition-colors hover:text-shell-on focus-visible:ring-2 focus-visible:ring-border-focused"
      >
        <span className="flex-1 text-left">{item.text}</span>
        <ChevronDownIcon
          className={cn(
            "size-3.5 shrink-0 transition-transform duration-200",
            isCollapsed && "-rotate-90",
          )}
          aria-hidden="true"
        />
      </button>
      <Collapse in={!isCollapsed} unmountOnExit>
        <ul className="m-0 flex list-none flex-col p-0">
          {item.items?.map((subItem) => {
            return (
              <li key={subItem.key}>
                <SideNavLeaf item={subItem} />
              </li>
            );
          })}
        </ul>
      </Collapse>
    </div>
  );
}

function SideNavSections({
  sections,
  isCollapsed,
  onToggle,
}: {
  sections: SideNavSection[];
  isCollapsed: (key: string) => boolean;
  onToggle: (key: string) => void;
}) {
  const mainSections = sections.filter(
    (section) => section.placement !== "footer",
  );
  const footerSections = sections.filter(
    (section) => section.placement === "footer",
  );

  return (
    <nav
      className="flex min-h-0 flex-1 flex-col"
      aria-label="Primary navigation"
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pt-1">
        {mainSections.map((section) => (
          <SideNavSectionBlock
            key={section.key}
            section={section}
            isCollapsed={isCollapsed}
            onToggle={onToggle}
          />
        ))}
      </div>
      {footerSections.length > 0 ? (
        <div className="shrink-0 border-t border-shell-border px-1.5 py-2">
          {footerSections.map((section) => (
            <SideNavSectionBlock
              key={section.key}
              section={section}
              isCollapsed={isCollapsed}
              onToggle={onToggle}
            />
          ))}
        </div>
      ) : null}
    </nav>
  );
}

function SideNavSectionBlock({
  section,
  isCollapsed,
  onToggle,
}: {
  section: SideNavSection;
  isCollapsed: (key: string) => boolean;
  onToggle: (key: string) => void;
}) {
  return (
    <div>
      {section.label ? (
        <div className="type-overline px-2.5 pb-1 pt-3 font-semibold text-shell-on-secondary">
          {section.label}
        </div>
      ) : null}
      {section.items.map((item) =>
        item.items?.length ? (
          <SideNavGroup
            key={item.key}
            item={item}
            isCollapsed={isCollapsed(item.key)}
            onToggle={onToggle}
          />
        ) : (
          <SideNavLeaf key={item.key} item={item} />
        ),
      )}
    </div>
  );
}

function SideNavTop({
  header,
  showClose,
  onClose,
}: Pick<SideNavBodyProps, "header" | "showClose" | "onClose">) {
  if (!header && !showClose) {
    return null;
  }
  return (
    <div className="flex h-(--shell-topbar-height) shrink-0 items-center gap-2 pl-3 pr-1">
      {header ? (
        <div className="min-w-0 flex-1 truncate">{header}</div>
      ) : (
        <span className="flex-1" />
      )}
      {showClose ? (
        <TopBarButton aria-label="Close navigation" onClick={onClose}>
          <XIcon className="size-4" aria-hidden="true" />
        </TopBarButton>
      ) : null}
    </div>
  );
}

function SideNavSearch({
  onSearch,
  searchTestId,
}: Pick<SideNavBodyProps, "onSearch" | "searchTestId">) {
  if (!onSearch) {
    return null;
  }
  return (
    <div className="px-2.5 pb-0.5 pt-1.5">
      <button
        type="button"
        onClick={onSearch}
        data-testid={searchTestId ?? "sidenav-search"}
        className="flex h-9 w-full items-center gap-2 rounded-lg border border-shell-border bg-transparent px-2.5 type-body-md text-shell-on-secondary outline-none transition-colors duration-150 hover:bg-surface-pressed hover:text-shell-on focus-visible:ring-2 focus-visible:ring-border-focused"
      >
        <SearchIcon className="size-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">Search</span>
      </button>
    </div>
  );
}

interface SideNavBodyProps {
  header?: ReactNode;
  sections: SideNavSection[];
  isCollapsed: (key: string) => boolean;
  onToggle: (key: string) => void;
  onSearch?: () => void;
  searchTestId?: string;
  showClose: boolean;
  onClose?: () => void;
}

function SideNavBody({
  header,
  sections,
  isCollapsed,
  onToggle,
  onSearch,
  searchTestId,
  showClose,
  onClose,
}: SideNavBodyProps) {
  return (
    <>
      <SideNavTop header={header} showClose={showClose} onClose={onClose} />
      <SideNavSearch onSearch={onSearch} searchTestId={searchTestId} />
      <SideNavSections
        sections={sections}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
      />
    </>
  );
}

interface SideNavShellProps extends SideNavProps {
  isCollapsed: (key: string) => boolean;
  toggle: (key: string) => void;
}

function DesktopSideNav({
  navRef,
  props,
}: {
  navRef: ForwardedRef<HTMLElement>;
  props: SideNavShellProps;
}) {
  return (
    <aside
      ref={navRef}
      data-testid={props.testId}
      className={cn(
        "hidden shrink-0 flex-col bg-shell-surface",
        !props.collapsed &&
          "lg:flex lg:w-(--sidebar-width) lg:border-r lg:border-shell-border",
        props.className,
      )}
    >
      <SideNavBody
        header={props.header}
        sections={props.sections}
        isCollapsed={props.isCollapsed}
        onToggle={props.toggle}
        onSearch={props.onSearch}
        searchTestId={props.searchTestId}
        showClose={false}
      />
    </aside>
  );
}

function MobileSideNav({ props }: { props: SideNavShellProps }) {
  return (
    <>
      <div
        aria-hidden="true"
        onClick={props.onClose}
        className={cn(
          "fixed inset-0 z-(--z-nav-overlay) bg-surface-overlay lg:hidden",
          props.open ? "block" : "hidden",
        )}
      />
      <aside
        inert={!props.open}
        aria-hidden={!props.open || undefined}
        data-testid={props.testId ? `${props.testId}-drawer` : undefined}
        className={cn(
          "fixed inset-y-0 left-0 z-(--z-nav-overlay) flex w-(--sidebar-width) flex-col bg-shell-surface shadow-panel transition-[transform,visibility] duration-300 ease-out lg:hidden",
          props.open ? "visible translate-x-0" : "invisible -translate-x-full",
        )}
      >
        <SideNavBody
          header={props.header}
          sections={props.sections}
          isCollapsed={props.isCollapsed}
          onToggle={props.toggle}
          onSearch={props.onSearch}
          searchTestId={props.searchTestId}
          showClose={true}
          onClose={props.onClose}
        />
      </aside>
    </>
  );
}

/**
 * Portal side navigation — two variants from one model, switched purely by
 * the lg breakpoint (no JS media query):
 * - lg+: in-flow column of width var(--sidebar-width) beside the content
 *   (`collapsed` hides it entirely — there is no icon rail)
 * - <lg: fixed overlay drawer translating in from the left over a
 *   --z-nav-overlay scrim; both `open` and `onClose` drive it
 *
 * Items with `items` render as collapsible groups via the Collapse
 * molecule; collapse state follows the portal's "only track
 * explicitly-collapsed keys" pattern (controlled `collapsedKeys` or
 * uncontrolled `defaultCollapsedKeys`).
 */
export const SideNav = forwardRef<HTMLElement, SideNavProps>((props, ref) => {
  const { isCollapsed, toggle } = useCollapsedGroups({
    collapsedKeys: props.collapsedKeys,
    defaultCollapsedKeys: props.defaultCollapsedKeys,
    onToggleCollapsed: props.onToggleCollapsed,
  });
  const shellProps = { ...props, isCollapsed, toggle };
  return (
    <>
      <DesktopSideNav navRef={ref} props={shellProps} />
      <MobileSideNav props={shellProps} />
    </>
  );
});
SideNav.displayName = "SideNav";
